import React from "react";
import { Link } from "react-router-dom";

import cx from "../ui/cx";
import { EmptyState } from "../ui/TableStateRow";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHeader,
} from "../ui/Table";
import { mobileCardClass, tableHeadCellClass } from "../ui/styles";
import { useAppSelector } from "../../app/hooks";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import {
  BusinessReportColumnType,
  BusinessReportLinkType,
  BusinessReportRowProps,
  BusinessReportSectionProps,
} from "../../types/businessReport";

const ledgerLinks: Record<
  BusinessReportLinkType,
  { path: string; permission: string }
> = {
  payments: { path: route.payments, permission: permission.paymentsRead },
  salaries: { path: route.salaries, permission: permission.salariesRead },
};

const formatValue = (
  value: number | null | undefined,
  type: BusinessReportColumnType
) => {
  if (value === null || value === undefined) {
    return "-";
  }

  const number = Number(value);

  if (type === "money") {
    return number.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  if (type === "weight") {
    return number.toLocaleString("en-IN", {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
  }

  if (type === "quantity") {
    return number.toLocaleString("en-IN", { maximumFractionDigits: 3 });
  }

  if (type === "minutes") {
    return `${Math.floor(number / 60)}:${String(number % 60).padStart(2, "0")}`;
  }

  return number.toLocaleString("en-IN");
};

const headRowClass =
  "border-b transition-colors data-[state=selected]:bg-muted bg-[#113F67]/5 hover:bg-[#113F67]/5";
const nameHeadClass =
  "h-12 px-4 text-left align-middle [&:has([role=checkbox])]:pr-0 text-[#113F67] font-semibold";
const numberHeadClass =
  "h-12 px-4 align-middle [&:has([role=checkbox])]:pr-0 text-right text-[#113F67] font-semibold whitespace-nowrap";
const bodyRowClass =
  "border-b transition-colors data-[state=selected]:bg-muted hover:bg-[#34699A]/5";
const footerRowClass =
  "border-b transition-colors data-[state=selected]:bg-muted bg-[#113F67]/10 font-bold hover:bg-[#113F67]/10";
const mobileTotalCardClass =
  "rounded-lg border text-card-foreground shadow-sm bg-[#113F67]/10";
const nameLinkClass = "font-semibold text-primary hover:underline break-words";
const cardRowClass = "flex items-start justify-between gap-4";
const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

type Props = {
  section: BusinessReportSectionProps;
  showTitle: boolean;
};

const BusinessReportTable = ({ section, showTitle }: Props) => {
  const loggingUser = useAppSelector(getLoggingUser);

  const link = section.link ? ledgerLinks[section.link] : null;
  const canOpen =
    !!link && hasPermission(loggingUser?.permissions, link.permission);

  const renderName = (row: BusinessReportRowProps) => (
    <>
      {canOpen && link && row.id ? (
        <Link
          to={link.path.replace(":id", String(row.id))}
          target="_blank"
          rel="noopener noreferrer"
          className={nameLinkClass}
        >
          {row.name}
        </Link>
      ) : (
        <span className="break-words">{row.name}</span>
      )}
      {row.sub && (
        <div className="mt-0.5 text-xs font-normal text-gray-500 break-words">
          {row.sub}
        </div>
      )}
    </>
  );

  const totalLines = section.footer.flatMap((footer) =>
    section.columns
      .filter(
        (column) =>
          footer.values[column.key] !== null &&
          footer.values[column.key] !== undefined
      )
      .map((column) => ({
        key: `${footer.key}-${column.key}`,
        label: footer.key === "total" ? column.label : footer.label,
        value: formatValue(footer.values[column.key], column.type),
      }))
  );

  return (
    <div className="space-y-3">
      {showTitle && (
        <div className="text-base font-semibold text-[#113F67]">
          {section.title}
        </div>
      )}

      {section.rows.length === 0 ? (
        <div className="rounded-lg border">
          <EmptyState label="આ સમયગાળામાં કોઈ એન્ટ્રી નથી." />
        </div>
      ) : (
        <>
          {section.count_label && (
            <div className="text-sm text-muted-foreground">
              {section.rows.length} {section.count_label}
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border max-sm:hidden">
            <Table>
              <TableHeader>
                <tr className={headRowClass}>
                  <th className={nameHeadClass}>{section.name_label}</th>
                  {section.columns.map((column) => (
                    <th key={column.key} className={numberHeadClass}>
                      {column.label}
                    </th>
                  ))}
                </tr>
              </TableHeader>

              <TableBody>
                {section.rows.map((row, index) => (
                  <tr key={`${row.id ?? row.name}-${index}`} className={bodyRowClass}>
                    <TableCell className="font-medium text-[#1f2937]">
                      {renderName(row)}
                    </TableCell>
                    {section.columns.map((column) => (
                      <TableCell
                        key={column.key}
                        className="text-right tabular-nums whitespace-nowrap"
                      >
                        {formatValue(row.values[column.key], column.type)}
                      </TableCell>
                    ))}
                  </tr>
                ))}
              </TableBody>

              {section.footer.length > 0 && (
                <TableFooter>
                  {section.footer.map((footer) => (
                    <tr key={footer.key} className={footerRowClass}>
                      <TableCell className="text-right text-[#113F67]">
                        {footer.label}
                      </TableCell>
                      {section.columns.map((column) => (
                        <TableCell
                          key={column.key}
                          className="text-right tabular-nums text-[#113F67] whitespace-nowrap"
                        >
                          {footer.values[column.key] === null ||
                          footer.values[column.key] === undefined
                            ? ""
                            : formatValue(
                                footer.values[column.key],
                                column.type
                              )}
                        </TableCell>
                      ))}
                    </tr>
                  ))}
                </TableFooter>
              )}
            </Table>
          </div>

          <div className="space-y-4 sm:hidden">
            {section.rows.map((row, index) => (
              <div key={`${row.id ?? row.name}-${index}`} className={mobileCardClass}>
                <div className="border-b p-4 font-medium text-[#1f2937]">
                  {renderName(row)}
                </div>
                <div className="space-y-3 p-4 text-sm">
                  {section.columns.map((column) => (
                    <div key={column.key} className={cardRowClass}>
                      <span className={cardLabelClass}>{column.label}</span>
                      <span className="text-right font-medium tabular-nums">
                        {formatValue(row.values[column.key], column.type)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {totalLines.length > 0 && (
              <div className={mobileTotalCardClass}>
                <div className="border-b p-4 font-bold text-[#113F67]">
                  {section.footer[0].label}
                </div>
                <div className="space-y-3 p-4 text-sm">
                  {totalLines.map((line) => (
                    <div key={line.key} className={cardRowClass}>
                      <span className={cardLabelClass}>{line.label}</span>
                      <span className="text-right font-bold tabular-nums text-[#113F67]">
                        {line.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
export default BusinessReportTable;
